import * as React from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** When set, render this instead of the default error UI when an error is caught. Use null to silently swallow. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Only capture in browser (Sentry may not be initialized during SSR)
    if (typeof window !== 'undefined' && typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.captureException(error, {
        tags: {
          component: 'ErrorBoundary',
          errorType: 'react-error-boundary',
        },
        extra: {
          componentStack: errorInfo.componentStack,
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      return (
        <div style={{ backgroundColor: 'orange', padding: '20px', color: 'white' }}>
          <h2>Something went wrong in InstantSearch:</h2>
          <pre>{this.state.error?.toString()}</pre>
        </div>
      );
    }

    return this.props.children;
  }
}
